import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  getDirectory, getPattis, createDirectoryEntry, updateDirectoryEntry, deleteDirectoryEntry,
  getGallery, addGalleryImage, deleteGalleryImage,
  getNews, createNews, updateNews, deleteNews,
  getEvents, createEvent, updateEvent, deleteEvent,
  getGrievances, updateGrievanceStatus,
  getServices, createService, deleteService,
  getTributes, updateTribute, deleteTribute
} from '../services/api';
import { adminContact } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Phone, User, Shield, Loader2, UserPlus, Lock,
  Image, Newspaper, Calendar, Users, X, MessageSquare, CheckCircle, Clock, Mail, Briefcase, Check, Flower2, Cake, Heart
} from 'lucide-react';

const AdminPage = () => {
  const { language } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('directory');
  
  // Directory state
  const [entries, setEntries] = useState([]);
  const [pattis, setPattis] = useState([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [addDirectoryOpen, setAddDirectoryOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ name: '', phone: '', patti_id: '', occupation: '' });
  
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [addGalleryOpen, setAddGalleryOpen] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', caption_en: '', caption_pa: '' });
  
  // News state
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [addNewsOpen, setAddNewsOpen] = useState(false);
  const [newNews, setNewNews] = useState({ 
    title_en: '', title_pa: '', content_en: '', content_pa: '', type: 'news', date: '' 
  });
  
  // Events state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({ 
    title_en: '', title_pa: '', description_en: '', description_pa: '',
    location_en: '', location_pa: '', date: '', time: ''
  });

  // Grievances state
  const [grievances, setGrievances] = useState([]);
  const [loadingGrievances, setLoadingGrievances] = useState(false);
  const [grievanceFilter, setGrievanceFilter] = useState('all');

  // Services state
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [newService, setNewService] = useState({ 
    name_en: '', name_pa: '', type: 'shop', phone: '', details_en: '', details_pa: ''
  });

  // Tributes state
  const [tributes, setTributes] = useState([]);
  const [loadingTributes, setLoadingTributes] = useState(false);
  const [tributeFilter, setTributeFilter] = useState('all');

  const ADMIN_PASSWORD = 'marhana2025';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Login successful');
    } else {
      toast.error('Invalid password');
    }
  };

  // Fetch functions
  const fetchDirectory = async () => {
    setLoadingDirectory(true);
    try {
      const [directoryData, pattiData] = await Promise.all([getDirectory(null, '', true), getPattis()]); // include_all=true for admin
      setEntries(directoryData);
      setPattis(pattiData);
    } catch (error) {
      toast.error('Failed to load directory');
    } finally {
      setLoadingDirectory(false);
    }
  };

  const fetchGallery = async () => {
    setLoadingGallery(true);
    try {
      const data = await getGallery();
      setGallery(data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoadingGallery(false);
    }
  };

  const fetchNews = async () => {
    setLoadingNews(true);
    try {
      const data = await getNews(50, true); // include_all=true for admin
      setNews(data);
    } catch (error) {
      toast.error('Failed to load news');
    } finally {
      setLoadingNews(false);
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const data = await getEvents(false, true); // include_all=true for admin
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchGrievances = async () => {
    setLoadingGrievances(true);
    try {
      const data = await getGrievances(grievanceFilter === 'all' ? null : grievanceFilter);
      setGrievances(data);
    } catch (error) {
      toast.error('Failed to load grievances');
    } finally {
      setLoadingGrievances(false);
    }
  };

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchTributes = async () => {
    setLoadingTributes(true);
    try {
      const typeFilter = tributeFilter === 'all' ? null : tributeFilter;
      const data = await getTributes(typeFilter, true); // include_all for admin
      setTributes(data);
    } catch (error) {
      toast.error('Failed to load tributes');
    } finally {
      setLoadingTributes(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDirectory();
      fetchGallery();
      fetchNews();
      fetchEvents();
      fetchGrievances();
      fetchServices();
      fetchTributes();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchGrievances();
    }
  }, [grievanceFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTributes();
    }
  }, [tributeFilter]);

  // Directory handlers
  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.name || !newEntry.phone || !newEntry.patti_id || !newEntry.occupation) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await createDirectoryEntry({
        name: newEntry.name,
        phone: newEntry.phone,
        patti_id: parseInt(newEntry.patti_id),
        occupation: newEntry.occupation
      }, true); // isAdmin=true - immediately approved
      toast.success('Entry added');
      setAddDirectoryOpen(false);
      setNewEntry({ name: '', phone: '', patti_id: '', occupation: '' });
      fetchDirectory();
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const handleApproveDirectoryEntry = async (id) => {
    try {
      await updateDirectoryEntry(id, { status: 'approved' });
      toast.success('Entry approved');
      fetchDirectory();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleRejectDirectoryEntry = async (id) => {
    if (!window.confirm('Reject this entry?')) return;
    try {
      await deleteDirectoryEntry(id);
      toast.success('Entry rejected');
      fetchDirectory();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteDirectoryEntry(id);
      toast.success('Entry deleted');
      fetchDirectory();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Gallery handlers
  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!newImage.url || !newImage.caption_en) {
      toast.error('Please provide URL and caption');
      return;
    }
    try {
      await addGalleryImage(newImage.url, newImage.caption_en, newImage.caption_pa || newImage.caption_en);
      toast.success('Image added');
      setAddGalleryOpen(false);
      setNewImage({ url: '', caption_en: '', caption_pa: '' });
      fetchGallery();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await deleteGalleryImage(id);
      toast.success('Image deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // News handlers
  const handleAddNews = async (e) => {
    e.preventDefault();
    if (!newNews.title_en || !newNews.content_en || !newNews.date) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await createNews({
        title_en: newNews.title_en,
        title_pa: newNews.title_pa || newNews.title_en,
        content_en: newNews.content_en,
        content_pa: newNews.content_pa || newNews.content_en,
        type: newNews.type,
        date: new Date(newNews.date).toISOString()
      }, true); // isAdmin=true - immediately approved
      toast.success('News added');
      setAddNewsOpen(false);
      setNewNews({ title_en: '', title_pa: '', content_en: '', content_pa: '', type: 'news', date: '' });
      fetchNews();
    } catch (error) {
      toast.error('Failed to add news');
    }
  };

  const handleApproveNews = async (id) => {
    try {
      await updateNews(id, { status: 'approved' });
      toast.success('News approved');
      fetchNews();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm('Delete this news?')) return;
    try {
      await deleteNews(id);
      toast.success('News deleted');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Event handlers
  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title_en || !newEvent.date || !newEvent.time) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await createEvent({
        title_en: newEvent.title_en,
        title_pa: newEvent.title_pa || newEvent.title_en,
        description_en: newEvent.description_en || '',
        description_pa: newEvent.description_pa || newEvent.description_en || '',
        location_en: newEvent.location_en || '',
        location_pa: newEvent.location_pa || newEvent.location_en || '',
        date: new Date(newEvent.date).toISOString(),
        time: newEvent.time
      }, true); // isAdmin=true - immediately approved
      toast.success('Event added');
      setAddEventOpen(false);
      setNewEvent({ title_en: '', title_pa: '', description_en: '', description_pa: '', location_en: '', location_pa: '', date: '', time: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteEvent(id);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleApproveEvent = async (id) => {
    try {
      await updateEvent(id, { status: 'approved' });
      toast.success('Event approved');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  // Grievance handlers
  const handleUpdateGrievanceStatus = async (id, newStatus) => {
    try {
      await updateGrievanceStatus(id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchGrievances();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Service handlers
  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.name_en || !newService.type) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await createService({
        name_en: newService.name_en,
        name_pa: newService.name_pa || newService.name_en,
        type: newService.type,
        phone: newService.phone || '',
        details_en: newService.details_en || '',
        details_pa: newService.details_pa || newService.details_en || ''
      });
      toast.success('Service added');
      setAddServiceOpen(false);
      setNewService({ name_en: '', name_pa: '', type: 'shop', phone: '', details_en: '', details_pa: '' });
      fetchServices();
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await deleteService(id);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      school: 'Education',
      hospital: 'Healthcare',
      shop: 'Services',
      religious: 'Religious',
      government: 'Government',
      ngo: 'NGO/Social'
    };
    return labels[type] || type;
  };

  // Tribute handlers
  const handleApproveTribute = async (id) => {
    try {
      await updateTribute(id, { status: 'approved' });
      toast.success('Tribute approved');
      fetchTributes();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleDeleteTribute = async (id) => {
    if (!window.confirm('Delete this tribute?')) return;
    try {
      await deleteTribute(id);
      toast.success('Tribute deleted');
      fetchTributes();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getTributeTypeLabel = (type) => {
    const labels = {
      antim_ardas: { en: 'Antim Ardas', pa: 'ਅੰਤਿਮ ਅਰਦਾਸ' },
      saskar: { en: 'Cremation', pa: 'ਸਸਕਾਰ' },
      birthday: { en: 'Birthday', pa: 'ਜਨਮਦਿਨ' },
      anniversary: { en: 'Anniversary', pa: 'ਵਿਆਹ ਦੀ ਵਰ੍ਹੇਗੰਢ' }
    };
    return labels[type] || { en: type, pa: type };
  };

  const getPattiName = (pattiId) => {
    const patti = pattis.find(p => p.id === pattiId);
    return patti ? (language === 'pa' ? patti.name_pa : patti.name_en) : '';
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input 
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                Login
              </Button>
            </form>
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500 mb-2">Contact for password:</p>
              <div className="flex items-center justify-center gap-2">
                <Shield className="h-4 w-4 text-amber-500" />
                <span className="font-medium">{adminContact.name}</span>
              </div>
              <a href={`tel:${adminContact.phone.replace(/\s/g, '')}`} className="text-amber-600 hover:underline text-sm">
                {adminContact.phone}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-800 to-gray-900 py-4 px-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-gray-400 text-xs">Marhana Village</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="text-white border-gray-600 hover:bg-gray-700" onClick={() => setIsAuthenticated(false)}>
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 mb-6">
            <TabsTrigger value="directory" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Directory</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
            <TabsTrigger value="tributes" className="flex items-center gap-2">
              <Flower2 className="h-4 w-4" />
              <span className="hidden sm:inline">Tributes</span>
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Gallery</span>
            </TabsTrigger>
            <TabsTrigger value="news" className="flex items-center gap-2">
              <Newspaper className="h-4 w-4" />
              <span className="hidden sm:inline">News</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Events</span>
            </TabsTrigger>
            <TabsTrigger value="grievances" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Grievances</span>
            </TabsTrigger>
          </TabsList>

          {/* DIRECTORY TAB */}
          <TabsContent value="directory">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{entries.length} entries</p>
              <Dialog open={addDirectoryOpen} onOpenChange={setAddDirectoryOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600"><UserPlus className="h-4 w-4 mr-2" />Add Person</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Person</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddEntry} className="space-y-4 mt-4">
                    <Input placeholder="Full Name *" value={newEntry.name} onChange={(e) => setNewEntry({...newEntry, name: e.target.value})} />
                    <Input placeholder="Phone *" value={newEntry.phone} onChange={(e) => setNewEntry({...newEntry, phone: e.target.value})} />
                    <Select value={newEntry.patti_id} onValueChange={(val) => setNewEntry({...newEntry, patti_id: val})}>
                      <SelectTrigger><SelectValue placeholder="Select Patti *" /></SelectTrigger>
                      <SelectContent>
                        {pattis.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name_en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Occupation *" value={newEntry.occupation} onChange={(e) => setNewEntry({...newEntry, occupation: e.target.value})} />
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setAddDirectoryOpen(false)}>Cancel</Button>
                      <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Add</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {loadingDirectory ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {entries.map((entry) => (
                  <Card key={entry.id} className={`border-0 shadow-md ${entry.status === 'pending' ? 'ring-2 ring-yellow-400' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          {entry.status === 'pending' && (
                            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 mb-1">
                              Pending Approval
                            </span>
                          )}
                          <h3 className="font-semibold">{entry.name}</h3>
                          <p className="text-xs text-amber-600">{getPattiName(entry.patti_id)}</p>
                          <p className="text-sm text-gray-500">{entry.phone}</p>
                          <p className="text-xs text-gray-400">{entry.occupation}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          {entry.status === 'pending' && (
                            <Button variant="ghost" size="sm" className="text-green-500 hover:text-green-700" onClick={() => handleApproveDirectoryEntry(entry.id)}>
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => entry.status === 'pending' ? handleRejectDirectoryEntry(entry.id) : handleDeleteEntry(entry.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SERVICES TAB */}
          <TabsContent value="services">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{services.length} services</p>
              <Dialog open={addServiceOpen} onOpenChange={setAddServiceOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600"><Plus className="h-4 w-4 mr-2" />Add Service</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New Service</DialogTitle>
                    <DialogDescription>Add a local service to the directory</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddService} className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                    <Input placeholder="Name (English) *" value={newService.name_en} onChange={(e) => setNewService({...newService, name_en: e.target.value})} />
                    <Input placeholder="Name (Punjabi)" value={newService.name_pa} onChange={(e) => setNewService({...newService, name_pa: e.target.value})} />
                    <Select value={newService.type} onValueChange={(val) => setNewService({...newService, type: val})}>
                      <SelectTrigger><SelectValue placeholder="Select Type *" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="school">Education</SelectItem>
                        <SelectItem value="hospital">Healthcare</SelectItem>
                        <SelectItem value="shop">Services/Shop</SelectItem>
                        <SelectItem value="religious">Religious Place</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="ngo">NGO/Social</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Phone Number" value={newService.phone} onChange={(e) => setNewService({...newService, phone: e.target.value})} />
                    <Input placeholder="Details (English)" value={newService.details_en} onChange={(e) => setNewService({...newService, details_en: e.target.value})} />
                    <Input placeholder="Details (Punjabi)" value={newService.details_pa} onChange={(e) => setNewService({...newService, details_pa: e.target.value})} />
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setAddServiceOpen(false)}>Cancel</Button>
                      <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Add</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {loadingServices ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => (
                  <Card key={service.id} className="border-0 shadow-md">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                            {getServiceTypeLabel(service.type)}
                          </span>
                          <h3 className="font-semibold mt-1">{service.name_en}</h3>
                          {service.name_pa && service.name_pa !== service.name_en && (
                            <p className="text-sm text-gray-500">{service.name_pa}</p>
                          )}
                          {service.details_en && (
                            <p className="text-xs text-gray-400 mt-1">{service.details_en}</p>
                          )}
                          {service.phone && (
                            <a href={`tel:${service.phone}`} className="text-sm text-amber-600 hover:underline flex items-center gap-1 mt-1">
                              <Phone className="h-3 w-3" /> {service.phone}
                            </a>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteService(service.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TRIBUTES TAB */}
          <TabsContent value="tributes">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{tributes.length} tributes</p>
              <Select value={tributeFilter} onValueChange={setTributeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="antim_ardas">Antim Ardas</SelectItem>
                  <SelectItem value="saskar">Cremation</SelectItem>
                  <SelectItem value="birthday">Birthday</SelectItem>
                  <SelectItem value="anniversary">Anniversary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {loadingTributes ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="space-y-4">
                {tributes.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Flower2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No tributes found</p>
                  </div>
                ) : (
                  tributes.map((tribute) => (
                    <Card key={tribute.id} className={`border-0 shadow-md ${tribute.status === 'pending' ? 'ring-2 ring-yellow-400' : ''}`}>
                      <CardContent className="p-5">
                        <div className="flex gap-4">
                          {tribute.image_url && (
                            <img src={tribute.image_url} alt={tribute.name} className="w-20 h-24 object-cover rounded-lg border-2 border-gray-200" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                tribute.type === 'antim_ardas' || tribute.type === 'saskar' 
                                  ? 'bg-gray-700 text-white' 
                                  : tribute.type === 'birthday' 
                                    ? 'bg-pink-100 text-pink-600' 
                                    : 'bg-red-100 text-red-600'
                              }`}>
                                {getTributeTypeLabel(tribute.type).en}
                              </span>
                              {tribute.status === 'pending' && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                  Pending Approval
                                </span>
                              )}
                            </div>
                            <h3 className="font-bold text-gray-900">{tribute.name}</h3>
                            {tribute.name_pa && tribute.name_pa !== tribute.name && (
                              <p className="text-sm text-gray-500">{tribute.name_pa}</p>
                            )}
                            {tribute.relation && (
                              <p className="text-sm text-gray-600">{tribute.relation}</p>
                            )}
                            <p className="text-sm text-amber-600 font-medium mt-1">
                              {new Date(tribute.event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                            {tribute.message && (
                              <p className="text-xs text-gray-500 mt-2 italic">"{tribute.message}"</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              Submitted by: {tribute.submitted_by} ({tribute.submitted_by_phone})
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            {tribute.status === 'pending' && (
                              <Button variant="ghost" size="sm" className="text-green-500 hover:text-green-700" onClick={() => handleApproveTribute(tribute.id)}>
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTribute(tribute.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          {/* GALLERY TAB */}
          <TabsContent value="gallery">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{gallery.length} images</p>
              <Dialog open={addGalleryOpen} onOpenChange={setAddGalleryOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600"><Plus className="h-4 w-4 mr-2" />Add Image</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Image</DialogTitle>
                    <DialogDescription>Enter image URL and caption</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddImage} className="space-y-4 mt-4">
                    <Input placeholder="Image URL *" value={newImage.url} onChange={(e) => setNewImage({...newImage, url: e.target.value})} />
                    <Input placeholder="Caption (English) *" value={newImage.caption_en} onChange={(e) => setNewImage({...newImage, caption_en: e.target.value})} />
                    <Input placeholder="Caption (Punjabi)" value={newImage.caption_pa} onChange={(e) => setNewImage({...newImage, caption_pa: e.target.value})} />
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setAddGalleryOpen(false)}>Cancel</Button>
                      <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Add</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {loadingGallery ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((img) => (
                  <div key={img.id} className="relative group">
                    <img src={img.url} alt={img.caption_en} className="w-full h-40 object-cover rounded-lg" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteImage(img.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{img.caption_en}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* NEWS TAB */}
          <TabsContent value="news">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{news.length} news items</p>
              <Dialog open={addNewsOpen} onOpenChange={setAddNewsOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600"><Plus className="h-4 w-4 mr-2" />Add News</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add News</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddNews} className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                    <Input placeholder="Title (English) *" value={newNews.title_en} onChange={(e) => setNewNews({...newNews, title_en: e.target.value})} />
                    <Input placeholder="Title (Punjabi)" value={newNews.title_pa} onChange={(e) => setNewNews({...newNews, title_pa: e.target.value})} />
                    <Textarea placeholder="Content (English) *" rows={3} value={newNews.content_en} onChange={(e) => setNewNews({...newNews, content_en: e.target.value})} />
                    <Textarea placeholder="Content (Punjabi)" rows={3} value={newNews.content_pa} onChange={(e) => setNewNews({...newNews, content_pa: e.target.value})} />
                    <Select value={newNews.type} onValueChange={(val) => setNewNews({...newNews, type: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="news">News</SelectItem>
                        <SelectItem value="announcement">Announcement</SelectItem>
                        <SelectItem value="health">Health</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="date" value={newNews.date} onChange={(e) => setNewNews({...newNews, date: e.target.value})} />
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setAddNewsOpen(false)}>Cancel</Button>
                      <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Add</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {loadingNews ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="space-y-3">
                {news.map((item) => (
                  <Card key={item.id} className={`border-0 shadow-md ${item.status === 'pending' ? 'ring-2 ring-yellow-400' : ''}`}>
                    <CardContent className="p-4 flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${item.type === 'announcement' ? 'bg-blue-100 text-blue-600' : item.type === 'health' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {item.type}
                          </span>
                          {item.status === 'pending' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                              Pending Approval
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold mt-1">{item.title_en}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2">{item.content_en}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(item.date).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-1">
                        {item.status === 'pending' && (
                          <Button variant="ghost" size="sm" className="text-green-500 hover:text-green-700" onClick={() => handleApproveNews(item.id)}>
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteNews(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EVENTS TAB */}
          <TabsContent value="events">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{events.length} events</p>
              <Dialog open={addEventOpen} onOpenChange={setAddEventOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600"><Plus className="h-4 w-4 mr-2" />Add Event</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Event</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddEvent} className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                    <Input placeholder="Title (English) *" value={newEvent.title_en} onChange={(e) => setNewEvent({...newEvent, title_en: e.target.value})} />
                    <Input placeholder="Title (Punjabi)" value={newEvent.title_pa} onChange={(e) => setNewEvent({...newEvent, title_pa: e.target.value})} />
                    <Textarea placeholder="Description (English)" rows={2} value={newEvent.description_en} onChange={(e) => setNewEvent({...newEvent, description_en: e.target.value})} />
                    <Textarea placeholder="Description (Punjabi)" rows={2} value={newEvent.description_pa} onChange={(e) => setNewEvent({...newEvent, description_pa: e.target.value})} />
                    <Input placeholder="Location (English)" value={newEvent.location_en} onChange={(e) => setNewEvent({...newEvent, location_en: e.target.value})} />
                    <Input placeholder="Location (Punjabi)" value={newEvent.location_pa} onChange={(e) => setNewEvent({...newEvent, location_pa: e.target.value})} />
                    <div className="grid grid-cols-2 gap-3">
                      <Input type="date" value={newEvent.date} onChange={(e) => setNewEvent({...newEvent, date: e.target.value})} />
                      <Input type="time" value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setAddEventOpen(false)}>Cancel</Button>
                      <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Add</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {loadingEvents ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="space-y-3">
                {events.map((event) => (
                  <Card key={event.id} className={`border-0 shadow-md ${event.status === 'pending' ? 'ring-2 ring-yellow-400' : ''}`}>
                    <CardContent className="p-4 flex justify-between items-start">
                      <div className="flex gap-4">
                        <div className="text-center bg-amber-100 rounded-lg px-3 py-2">
                          <p className="text-xs text-amber-600">{new Date(event.date).toLocaleDateString('en', { month: 'short' })}</p>
                          <p className="text-xl font-bold text-amber-700">{new Date(event.date).getDate()}</p>
                        </div>
                        <div>
                          {event.status === 'pending' && (
                            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 mb-1">
                              Pending Approval
                            </span>
                          )}
                          <h3 className="font-semibold">{event.title_en}</h3>
                          <p className="text-sm text-gray-500">{event.time} • {event.location_en}</p>
                          <p className="text-xs text-gray-400 mt-1">{event.description_en}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {event.status === 'pending' && (
                          <Button variant="ghost" size="sm" className="text-green-500 hover:text-green-700" onClick={() => handleApproveEvent(event.id)}>
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteEvent(event.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* GRIEVANCES TAB */}
          <TabsContent value="grievances">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-500">{grievances.length} grievances</p>
              <Select value={grievanceFilter} onValueChange={setGrievanceFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {loadingGrievances ? <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /> : (
              <div className="space-y-4">
                {grievances.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No grievances found</p>
                  </div>
                ) : (
                  grievances.map((grievance) => (
                    <Card key={grievance.id} className="border-0 shadow-md">
                      <CardContent className="p-5">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(grievance.status)}`}>
                                {grievance.status?.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                {grievance.category}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">{grievance.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-gray-400" />
                                <a href={`tel:${grievance.phone}`} className="text-amber-600 hover:underline text-sm">
                                  {grievance.phone}
                                </a>
                              </div>
                              {grievance.email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <a href={`mailto:${grievance.email}`} className="text-amber-600 hover:underline text-sm">
                                    {grievance.email}
                                  </a>
                                </div>
                              )}
                            </div>
                            <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm">
                              {grievance.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Submitted: {new Date(grievance.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Select 
                              value={grievance.status} 
                              onValueChange={(val) => handleUpdateGrievanceStatus(grievance.id, val)}
                            >
                              <SelectTrigger className="w-32 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-3 w-3" /> Pending
                                  </div>
                                </SelectItem>
                                <SelectItem value="in_progress">
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="h-3 w-3" /> In Progress
                                  </div>
                                </SelectItem>
                                <SelectItem value="resolved">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="h-3 w-3" /> Resolved
                                  </div>
                                </SelectItem>
                                <SelectItem value="rejected">
                                  <div className="flex items-center gap-2">
                                    <X className="h-3 w-3" /> Rejected
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
